/**
 * Redux Store設定
 *
 * 【変更点】
 * - RTK Query の api.reducer と api.middleware を追加
 * - authSlice（認証状態管理）を追加
 * - useAppDispatch / useAppSelector を追加（型安全な Redux hooks）
 */
import { configureStore, createSlice } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import storeSelectionReducer from './storeSelectionSlice';
import menuReducer from './menuSlice';
import notificationReducer from './notificationSlice';
import { api } from '../../shared/store/api';
import authReducer from '../../shared/store/slices/authSlice';

export { setStoreSelection, clearStoreSelection } from './storeSelectionSlice';
export type { StoreSelectionState, StoreSelectionItem } from './storeSelectionSlice';
export { setMenus } from './menuSlice';
export { setHasUnread } from './notificationSlice';

// 一時的なダミーslice（警告回避用）
const appSlice = createSlice({
  name: 'app',
  initialState: {
    initialized: true,
  },
  reducers: {},
});

export const store = configureStore({
  reducer: {
    app: appSlice.reducer,
    storeSelection: storeSelectionReducer,
    menu: menuReducer,
    notification: notificationReducer,
    // RTK Query のキャッシュデータを保存する reducer
    [api.reducerPath]: api.reducer,
    // 認証状態（user, isAuthenticated）を管理する reducer
    auth: authReducer,
  },
  middleware: (getDefaultMiddleware) =>
    // RTK Query の middleware を追加
    // （キャッシュの期限管理、ポーリング、リフレッシュ等の裏方処理を担当）
    getDefaultMiddleware().concat(api.middleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

/**
 * 型安全な Redux hooks
 * useDispatch() / useSelector() を直接使うと型推論が効かないため、
 * 型付きの hooks を用意しておく（Redux 公式推奨パターン）。
 */
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
